import React, { useContext, useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import classnames from "classnames";
import styles from "./CouponOptInOffers.scss";
import filter from "lodash/filter";
import find from "lodash/find";
import keys from "lodash/keys";
import * as CouponActions from "actions/CouponActions";
import { windowSizeContext } from "AppRoot";
import AccessDenied from "components/common/AccessDenied";
import Button from "components/common/Button";
import CouponChangeHistoryTable from "components/admin/widgets/CouponChangeHistoryTable";
import Modal from "components/common/Modal";
import OfferGroupAccordion from "components/coupons/elements/OfferGroupAccordion";
import SelectedOffer from "components/coupons/elements/SelectedOffer";
import { PermissionTypes, userHasPermission } from "utils/permissions/rolesPermissions";
import * as UserPermissions from "constants/UserPermissions";

function CouponOptInOffers(props) {
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [selectedOfferId, setSelectedOfferId] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [showTerms, setShowTerms] = useState(false);

    const context = useContext(windowSizeContext) || {};

    const fetchData = () => {
        if (props.canViewClinicCouponLibrary && !(props.program?.provider)) {
            props.getPrograms(props.clinicId);
        }
    }

    useEffect(() => {
        fetchData();
    }, [props.clinicId]);

    if(!props.canViewClinicCouponLibrary) {
        return <AccessDenied/>;
    }

    const loadCouponHistory = () => {
        if (!props.offerHistory) {
            props.getProgramOfferHistory(props.clinicId, props.programId);
        }
        setShowHistory(!showHistory);
    }

    const handleOfferChanged = (offer) => {
        if(offer) {
            setSelectedOfferId(offer.offerId);
            setSelectedGroupId(offer.groupId);
        } else {
            setSelectedOfferId(null);
            setSelectedGroupId(null);
        }
    }

    const handleHeaderClicked = (groupId, isOpen) => {
        setSelectedGroupId(isOpen ? groupId : null);
        setSelectedOfferId(null);

        if (props.onSelect) {
            props.onSelect(groupId);
        }
    }

    const selectedOfferGroup = selectedGroupId ? find(props.offerGroups, {groupId: selectedGroupId}) : null;
    const offer = (selectedOfferGroup && selectedOfferId) ? find(selectedOfferGroup.offers, {offerId: selectedOfferId}) : null;
    const offerGroupCount = keys(props.offerGroups).length;

    return (
        <div className={styles.root}>
            <div className="flex spaced-content">
                <OfferGroupAccordion
                    offerId={selectedOfferId}
                    programId={props.programId}
                    headerClicked={handleHeaderClicked}
                    offerChanged={handleOfferChanged}
                    isReadOnlyView={props.isReadOnlyView}
                    searchQuery={props.searchQuery}
                />
            
                <div className={classnames(styles.offerDetails, {
                    [styles.selected]: !!selectedOfferId
                })}>
                    {context.isPhone && (
                        <div className="flex-none flex flex-end">
                            <Button
                                onClick={() => handleOfferChanged(null)}
                                icon
                                type="primary"
                            >
                                <i className="fa fa-reply"/> Back to Coupon Selection
                            </Button>
                        </div>
                    )}
                    {!!props.canViewClinicCouponLibraryHistory &&
                        <div className="flex-none flex spaced-content">
                            <Button
                                onClick={loadCouponHistory}
                                icon
                                type="primary"
                            >
                                <i className="fa fa-history" />See {(props.program && props.program.provider) && props.program.provider.name} Coupon Change History
                            </Button>
                        </div>
                    }
                    {selectedOfferId ? (
                        <SelectedOffer
                            programId={props.programId}
                            offerId={selectedOfferId}
                            offerGroupId={selectedGroupId}
                        />
                    ) : (
                        <div className={styles.offerHeader}>
                            {offerGroupCount === 0 && (
                                <div>No offers available</div>
                            )}
                            {offerGroupCount > 0 && (
                                <div>Click an offer to view it</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <Modal
                show={showHistory}
                onClose={() => setShowHistory(false)}
                modalTitle={`${(props.program && props.program.provider) ? props.program.provider.name : ""} Coupon Change History`}
                icon="fa-history"
                medium
            >
                {props.canViewClinicCouponLibraryHistory ? (
                    <CouponChangeHistoryTable
                        clinicId={props.clinicId}
                        providerId={props.programId}
                    />
                ) : <AccessDenied/>}
            </Modal>
            <Modal
                show={showTerms}
                modalTitle="Special Instructions"
                onClose={() => setShowTerms(false)}
            >
                {offer && offer.terms && <span dangerouslySetInnerHTML={{ __html: offer.terms }} />}
            </Modal>
        </div>
    );
}

CouponOptInOffers.propTypes = {
    programId: PropTypes.number.isRequired,
    clinicId: PropTypes.number.isRequired,
    isReadOnlyView: PropTypes.bool, // Disable interaction with opt-in elements
}

export default connect(
    (state, ownProps) => {
        const userProfile = state.user.userProfile;
        const offerGroups = state.entities.programOffers[ownProps.programId]?.offers || null;
        //Permissions
        const canViewClinicCouponLibrary = userHasPermission(PermissionTypes.VIEW, UserPermissions.CLINIC_COUPON_LIBRARY, userProfile);
        const canViewClinicCouponLibraryHistory = userHasPermission(PermissionTypes.HISTORY, UserPermissions.CLINIC_COUPON_LIBRARY, userProfile);
        const canOptInCouponLibrary = userHasPermission(PermissionTypes.OPT_IN, UserPermissions.CLINIC_COUPON_LIBRARY, userProfile);
        const clinicCouponOfferHistories = state.entities.couponOfferHistory[ownProps.clinicId] ? state.entities.couponOfferHistory[ownProps.clinicId] : {};
        return {
            canViewClinicCouponLibrary,
            canOptInCouponLibrary,
            canViewClinicCouponLibraryHistory,
            program: state.entities.programs[ownProps.programId],
            //Filter the offerGroups that don't have any "isVisibleToClinic" offers
            offerGroups:  !!offerGroups ? filter(offerGroups, offerGroup => !!filter(offerGroup.offers, {isVisibleToClinic: true})?.length) : null,
            offerHistory: (clinicCouponOfferHistories && clinicCouponOfferHistories[ownProps.programId]) ? clinicCouponOfferHistories[ownProps.programId] : null,
            searchQuery: state.entities.genericSearch,
        }
    },
    (dispatch) => ({
        getPrograms: (clinicId) => dispatch(CouponActions.getPrograms(clinicId)),
        getProgramOfferHistory: (clinicId, providerId) => dispatch(CouponActions.getCouponOfferHistory(clinicId, providerId))
    }),
)(CouponOptInOffers);
